import { Router } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Component, Inject, OnDestroy } from '@angular/core';

import { ShopKeys } from '@config/keys/shop.keys';
import { OrderKeys } from '@config/keys/order.keys';
import { LineItemKeys } from '@config/keys/line-item.keys';

import { SlugUtils } from '@shared/utils/slug/slug.utils';
import { DateUtils } from '@shared/utils/date/date.utils';

import { Paths } from '@config/paths.config';
import { OrderTypeValues } from '@config/values/order.values';
import { OrderStatusLabels } from '@config/labels/order.labels';

import { DoodShopModel } from '@core/models/shop.model';

import { HeaderService } from '@core/services/header/header.service';
import { RouterHelperService } from 'src/app/core/services/router-helper/router-helper.service';
import { CartService } from '@core/services/cart/cart.service';
import { DoodOrderLineItemModel, DoodOrderModel } from '@store/order/order.model';
import { DoodLineItemModel } from '@core/models/line-item.model';

import {
  ORDER_DETAILS_BLOCK_DATA_SOURCE,
  OrderDetailsBlockDataSource,
  OrderDetailsBlockViewModel,
} from './order-details-block-view-model';
import { provideOrderDetailsBlockDataSource } from './order-details-block-data-source.providers';

@Component({
  selector: 'app-order-details-block',
  templateUrl: './order-details-block.component.html',
  standalone: false,
  providers: [...provideOrderDetailsBlockDataSource()],
})
export class OrderDetailsBlockComponent implements OnDestroy {
  protected readonly orderDetailsVm$: Observable<OrderDetailsBlockViewModel>;

  orderReadyAt!: string;

  orderKeys = OrderKeys;
  shopKeys = ShopKeys;
  lineItemKeys = LineItemKeys;
  orderStatusLabels = OrderStatusLabels;
  orderTypeValues = OrderTypeValues;

  constructor(
    private readonly router: Router,
    private readonly headerService: HeaderService,
    private readonly routerHelper: RouterHelperService,
    private readonly translateService: TranslateService,
    private readonly cartService: CartService,
    @Inject(ORDER_DETAILS_BLOCK_DATA_SOURCE) blockDataSource: OrderDetailsBlockDataSource,
  ) {
    this.headerService.setTitle('order-details.order');
    this.orderDetailsVm$ = combineLatest([
      blockDataSource.vm$,
      this.translateService.get(['order-details.format', 'order-details.at', 'date.short-format']),
    ]).pipe(
      tap(([vm, translations]) => {
        const order = vm.order;
        if (!order?.ready_at) {
          this.orderReadyAt = '';
          return;
        }
        this.orderReadyAt = `${DateUtils.formatLocalizedDate(order.ready_at, translations['date.short-format'])} ${translations['order-details.at']} ${DateUtils.dayjsInMarketplaceTimezone(order.ready_at).format(translations['order-details.format'])}`;
      }),
      map(([vm]) => vm),
    );
  }

  ngOnDestroy(): void {
    this.headerService.setTitle(undefined);
  }

  navigateToShop(shop: DoodShopModel, _marketplaceId: string, isMultiShop: boolean): void {
    if (!isMultiShop) {
      this.router.navigate([
        this.routerHelper.translateRoute(
          `${Paths.Shops}/${SlugUtils.formatSlug(shop[ShopKeys.Name])}/${shop[ShopKeys.Slug]}`,
        ),
      ]);
      return;
    }
    this.router.navigate([this.routerHelper.translateRoute(Paths.Shops)]);
  }

  renewOrder(order: DoodOrderModel): void {
    this.cartService.createCartFromOrder(order).subscribe(() => {
      this.router.navigate([this.routerHelper.translateRoute(Paths.Cart)]);
    });
  }

  trackByOrderId(_index: number, order: { id: string }): string {
    return order.id;
  }

  goToMap(): void {
    this.router.navigate([this.routerHelper.translateRoute(Paths.ShopsMap)]);
  }

  protected asDoodLineItemModel(items: DoodOrderLineItemModel[]): DoodLineItemModel[] {
    return items as DoodLineItemModel[];
  }
}
